import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Search, Warehouse, AlertTriangle, Truck, ShieldCheck, Link2, Loader, X,
    FileSpreadsheet, ChevronRight, MapPin, Table2, Users, PackageCheck, ClipboardCheck, Hourglass, Maximize2,
} from "lucide-react";
import api from "../../../utils/api";
import { OUTBOUND_DC, outboundDestination, stateFromPostcode } from "./constants";
import ShipmentMap from "./ShipmentMap";
import ShipmentContacts from "./ShipmentContacts";

// Outbound view, split into the two phases of an outbound order:
//   Dispatch — in the warehouse, up to the physical handover to transport (Outstanding Orders report)
//   Shipped  — the HSB consignment is with the carrier on its way to the customer (carrier report)
// joined on the 10-digit order reference. Tables open in overlays so the page stays one screen.
// Data: /api/excel-ingestion/outbound-link and /order-timeline (outboundLink.js on the backend).

const card = "bg-[#111827] border border-[#1F2937] rounded-xl";
const STAGE_COLOR = {
    WH_ALLOCATED: "#64748B", WH_RELEASED: "#8B5CF6", WH_IN_PROGRESS: "#F59E0B",
    WH_PICKED: "#3B82F6", WH_READY_TO_LOAD: "#10B981", WH_COMPLETE: "#059669",
};
const SEVERITY = {
    issue: "bg-red-500/15 text-red-300 border-red-500/30",
    watch: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    ok: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
};
const fmtDate = iso => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const num = n => (n ?? 0).toLocaleString();
// Customer PO compare (same rule as the backend search): case, spaces and dashes ignored, O = 0, I = 1
const poLoose = v => String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/O/g, "0").replace(/I/g, "1");
const poMatch = (po, s) => poLoose(s).length >= 4 && poLoose(po).includes(poLoose(s));
const th = "px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap text-left";
const inputDark = "bg-[#0D1520] border border-[#1F2937] rounded-lg text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/60";

const dayMs = 86400000;
const daysFrom = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / dayMs);
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

/** Lead time of an order: pick-up (or ship-by) → required delivery, and the days left until delivery */
function leadTimeOf(m) {
    const shipBy = m.orders.map(o => o.shipBy).filter(Boolean).sort()[0] || null;
    const pickup = m.mfexport?.pickupFrom || m.prealert?.pickup || shipBy;
    const delivery = m.departed?.requiredDelivery || m.mfexport?.delivery || m.prealert?.delivery || null;
    if (!pickup && !delivery) return null;
    const delivered = m.departed?.status === "DELIVERED" || m.steps?.some(s => s.key === "delivered" && s.state === "done");
    return {
        pickup, delivery, delivered,
        days: pickup && delivery ? daysFrom(pickup, delivery) : null,
        left: delivery && !delivered ? daysFrom(todayIso(), delivery) : null,
    };
}

/** Timeline match → map focus: at the DC while in dispatch, DC → destination once shipped */
export function mapFocusFor(m) {
    // Shipped = with the carrier (carrier report departed row, or TNT Daily)
    const dep = m.departed || m.tnt;
    const state = dep ? (dep.status === "DELIVERED" ? "delivered" : dep.severity === "issue" ? "issue" : "transit") : "warehouse";
    const destination = m.departed ? outboundDestination({ site: m.departed.site, suburb: m.departed.suburb })
        : m.tnt ? outboundDestination({ state: stateFromPostcode(m.tnt.postcode), suburb: m.tnt.suburb })
        : m.mfexport ? outboundDestination({ state: m.mfexport.state || stateFromPostcode(m.mfexport.postcode), suburb: m.mfexport.city })
        : m.prealert ? outboundDestination({ state: m.prealert.state, suburb: m.prealert.city })
        : m.prebooking ? outboundDestination({ state: m.prebooking.state }) : null;
    const note = dep && !destination
        ? `Destination ${dep.site || dep.suburb || ""} isn't on the map yet — site code not recognised`
        : state === "warehouse" ? `At ${OUTBOUND_DC.name}${m.warehouseStage ? ` · ${m.warehouseStage}` : ""}`
        : destination ? `${destination.detail ? `${destination.detail} · ` : ""}pin is the delivery site's city` : null;
    return {
        title: dep?.connote || m.consignment || m.orderReference,
        sub: [m.customer, dep ? dep.statusLabel : m.warehouseStage, m.po && `PO ${m.po}`].filter(Boolean).join(" · "),
        state, destination, note, orderReference: m.orderReference, lead: leadTimeOf(m),
    };
}

function SectionTitle({ icon: Icon, title, sub, right }) {
    return (
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
            <div className="flex items-start gap-2.5">
                <Icon size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <div>
                    <p className="text-sm font-bold text-white">{title}</p>
                    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
                </div>
            </div>
            {right}
        </div>
    );
}

function Kpi({ label, value, sub, tone = "text-white", icon: Icon, onClick }) {
    const Comp = onClick ? "button" : "div";
    return (
        <Comp onClick={onClick} className={`${card} px-4 py-3.5 text-left ${onClick ? "hover:border-slate-600 transition-colors group" : ""}`}>
            <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-500">{label}</span>
                {onClick ? <Maximize2 size={12} className="text-slate-600 group-hover:text-blue-400" /> : Icon && <Icon size={14} className="text-slate-600" />}
            </div>
            <p className={`text-2xl font-bold mt-1 tabular-nums ${tone}`}>{value}</p>
            {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
        </Comp>
    );
}

function StagePill({ code, label }) {
    const c = STAGE_COLOR[code] || "#64748B";
    return <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap" style={{ background: `${c}26`, color: c }}>{label}</span>;
}

function OpenButton({ icon: Icon = Table2, children, onClick }) {
    return (
        <button onClick={onClick} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#334155] text-xs text-slate-300 hover:border-blue-500/60 hover:text-white hover:bg-blue-500/10 transition-colors">
            <Icon size={13} /> {children}
        </button>
    );
}

// Full-screen overlay for a table (Esc or ✕ closes); `fit` sizes it to small content instead.
// z-index is above the Leaflet maps.
export function Overlay({ title, sub, onClose, toolbar, children, fit = false }) {
    useEffect(() => {
        const onKey = e => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className={`fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm flex justify-center p-3 sm:p-6 ${fit ? "items-center" : "items-stretch"}`} onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div className={`w-full max-w-[1400px] bg-[#0B1120] border border-[#1F2937] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${fit ? "max-h-full" : ""}`}>
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#1F2937] shrink-0">
                    <div>
                        <p className="text-base font-bold text-white">{title}</p>
                        {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10" title="Close (Esc)"><X size={18} /></button>
                </div>
                {toolbar && <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-[#1F2937] shrink-0">{toolbar}</div>}
                <div className="flex-1 overflow-auto">{children}</div>
            </div>
        </div>
    );
}

// contacts: show the customer / carrier contacts and "Request update" on each order (Hasbro workspace)
export default function OutboundInsights({ refreshKey, onUpload, onSelect, selectedRef, focus, onClearFocus, contacts = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [timeline, setTimeline] = useState(null);   // { query, matches } | { loading } | { error }
    const [overlay, setOverlay] = useState(null);     // { kind: "orders"|"shipped"|"customers", preset }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/api/excel-ingestion/outbound-link");
            setData(data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || "Couldn't load the outbound reports");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load, refreshKey]);

    const findOrder = useCallback(async q => {
        const text = String(q || "").trim();
        setQuery(text);
        if (text.length < 3) return setTimeline(null);
        setTimeline({ loading: true });
        try {
            const { data } = await api.get("/api/excel-ingestion/order-timeline", { params: { q: text } });
            setTimeline(data);
            if (data.matches?.[0]) onSelect?.(mapFocusFor(data.matches[0]));
        } catch (e) {
            setTimeline({ error: e.response?.data?.error || "Search failed" });
        }
    }, [onSelect]);

    // A row picked in an overlay: close it and show that order in the timeline + map
    const pick = useCallback(q => { setOverlay(null); findOrder(q); }, [findOrder]);

    if (loading && !data) return <div className={`${card} h-28 flex items-center justify-center text-slate-500 text-sm`}><Loader size={16} className="animate-spin mr-2" />Loading outbound reports…</div>;
    if (error) return <div className="bg-red-900/30 border border-red-800 rounded-xl px-4 py-3 text-sm text-red-400">Outbound reports: {error}</div>;

    const hasOrders = !!data?.reports?.outstanding;
    const hasPrealert = !!data?.reports?.prealert;
    const hasMfExport = !!data?.reports?.mfexport;
    const hasShipped = !!(data?.reports?.outbound || data?.reports?.tnt);
    const t = data.pipeline.totals;
    const loaded = ["outstanding", "prealert", "tnt", "outbound"].map(k => data.reports?.[k]).filter(Boolean);

    return (
        <div className="space-y-4">
            <OutboundKpis data={data} hasOrders={hasOrders} hasPrealert={hasPrealert} hasMfExport={hasMfExport} hasShipped={hasShipped}
                open={(kind, preset = {}) => setOverlay({ kind, preset })} />

            {/* ── Dispatch: in the warehouse until handed to transport ── */}
            <div className={`${card} px-5 py-4 flex items-center gap-4`}>
                <span className="w-11 h-11 rounded-xl grid place-items-center shrink-0 bg-blue-500 text-white"><Warehouse size={20} /></span>
                <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                        <span className="text-base font-bold text-white">Dispatch</span>
                        {hasOrders && t.notPicked > 0 && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${SEVERITY.watch}`}>{num(t.notPicked)} not picked</span>}
                    </span>
                    <span className="block text-[11px] text-slate-500">In the warehouse, until handed to transport</span>
                    <span className="block text-xs text-slate-300 mt-1">{hasOrders ? `${num(t.orders)} orders · ${num(t.references)} shipments${hasPrealert || hasMfExport ? ` · ${num(data.mainfreight?.shipments)} with Mainfreight` : ""}` : "No Outstanding Orders upload yet"}</span>
                </span>
            </div>

            {/* ── One status per shipment across every loaded report (rules: OUTBOUND_STATUS_ORDER) ── */}
            {data.master?.shipments > 0 && (
                <div className={`${card} px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs`}>
                    <span className="font-bold text-white">{num(data.master.shipments)} shipments</span>
                    {[["atThreePlOnly", "at 3PL only", "text-slate-300"], ["atThreePlAndPrebooked", "at 3PL + pre-booked", "text-sky-300"],
                      ["prebookedOnly", "pre-booked, not in Outstanding", "text-amber-300"], ["shippedNotOutstanding", "shipped", "text-emerald-300"],
                      ["shippedStillOutstanding", "shipped but still outstanding", "text-red-400"]].map(([k, label, tone]) => (
                        data.master.combos[k] > 0 && <span key={k} className={tone}><span className="font-bold tabular-nums">{num(data.master.combos[k])}</span> {label}</span>
                    ))}
                    <span className="ml-auto text-[11px] text-slate-500">From {loaded.map(r => `${r.label} (${fmtDate(r.reportDate)})`).join(" · ")}</span>
                </div>
            )}

            {/* ── Order timeline search, with the selected shipment on the map beside it ── */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                <div className={`${card} xl:col-span-7`}>
                    <SectionTitle icon={Search} title="Find an order"
                        sub="Shipment number, consignment, delivery id, PO / customer PO, or customer name — across every loaded report" />
                    <form onSubmit={e => { e.preventDefault(); findOrder(query); }} className="px-5 pb-4 flex gap-2">
                        <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g. 2994080, HSB000074463, 0051989953, 0453780779, 2O764JYT…"
                                className={`w-full ${inputDark} text-sm pl-9 pr-8 py-2`} />
                            {query && <button type="button" onClick={() => { setQuery(""); setTimeline(null); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X size={14} /></button>}
                        </div>
                        <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold text-white">Find</button>
                    </form>
                    {timeline ? (
                        <div className="px-5 pb-5 space-y-3 max-h-[520px] overflow-auto">
                            {timeline.loading && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader size={14} className="animate-spin" /> Searching…</p>}
                            {timeline.error && <p className="text-sm text-red-400">{timeline.error}</p>}
                            {timeline.matches && !timeline.matches.length && !timeline.inbound?.length && <p className="text-sm text-slate-500">Nothing found for “{timeline.query}” in the current reports.</p>}
                            {timeline.inbound?.length > 0 && <InboundMatches rows={timeline.inbound} />}
                            {timeline.matches?.map(m => (
                                <TimelineCard key={m.orderReference} m={m} onSelect={onSelect && (() => onSelect(mapFocusFor(m)))} selected={selectedRef === m.orderReference} contacts={contacts} />
                            ))}
                        </div>
                    ) : (
                        <p className="px-5 pb-5 text-[12px] text-slate-500">Or click a row in any table — it opens here and on the map.</p>
                    )}
                </div>
                <ShipmentMap className="xl:col-span-5" minHeight={300} title="Where is it?"
                    subtitleText={focus ? (focus.state === "warehouse" ? "Dispatch — still at the DC" : "Shipped — from the DC to the customer") : `${OUTBOUND_DC.name} · select an order to see it here`}
                    resolvedPins={[]} routes={[]} rawLocs={[]} unmapped={[]} hasExcel dc={OUTBOUND_DC} focus={focus} onClearFocus={onClearFocus} />
            </div>

            {/* ── Phase content ── */}
            {hasOrders ? <DispatchPanel data={data} open={setOverlay} /> : (
                <MissingReport onUpload={onUpload} title="Dispatch"
                    text="Upload the Outstanding Orders export (CSV) to see orders by warehouse stage, what isn't picked yet and what is overdue." />
            )}
            {/* Consignments with transport — only once a TNT Daily or carrier report is loaded */}
            {hasShipped && <ShippedPanel shipped={data.shipped} open={setOverlay} />}

            <LinkChecks checks={data.checks} hasBoth={hasOrders && hasShipped} />

            {overlay?.kind === "orders" && <OrdersOverlay data={data} preset={overlay.preset} onClose={() => setOverlay(null)} onPick={pick} />}
            {overlay?.kind === "customers" && <CustomersOverlay customers={data.pipeline.customers} onClose={() => setOverlay(null)} />}
            {overlay?.kind === "shipped" && <ShippedOverlay shipped={data.shipped} preset={overlay.preset} onClose={() => setOverlay(null)} onPick={pick} />}
            {overlay?.kind === "mfexport" && <MfExportOverlay mf={data.mfexport} report={data.reports.mfexport} preset={overlay.preset} onClose={() => setOverlay(null)} onPick={pick} />}
            {overlay?.kind === "prealert" && <PrealertOverlay prebooked={data.prebooked} report={data.reports.prealert} preset={overlay.preset} onClose={() => setOverlay(null)} onPick={pick} />}
        </div>
    );
}

// Top metric row for the Outbound view, from whichever outbound reports are loaded
// (Outstanding Orders, Mainfreight Prealert, TNT Daily, carrier report). Click = the matching table.
function OutboundKpis({ data, hasOrders, hasPrealert, hasMfExport, hasShipped, open }) {
    const t = data.pipeline.totals;
    const pct = (n, of) => (of ? `${Math.round((n / of) * 100)}%` : "0%");
    const cards = [
        { icon: Warehouse, color: "#3B82F6", label: "Shipments", value: data.master?.shipments || null,
          sub: hasOrders ? `${num(t.orders)} orders · ${num(t.units)} units in dispatch` : "Across the loaded outbound reports", kind: hasOrders ? "orders" : null },
        { icon: Hourglass, color: "#F59E0B", label: "Not picked yet", value: hasOrders ? t.notPicked : null,
          sub: hasOrders ? `${pct(t.notPicked, t.orders)} of orders · ${num(t.notPickedUnits)} units` : "Needs Outstanding Orders", kind: "orders", preset: { only: "notPicked" } },
        { icon: AlertTriangle, color: "#EF4444", label: "Past ship-by", value: hasOrders ? t.overdue : null,
          sub: hasOrders ? (t.noShipBy ? `${t.noShipBy} with no ship-by date` : "All orders have a ship-by date") : "Needs Outstanding Orders", kind: "orders", preset: { only: t.overdue ? "overdue" : "noShipBy" } },
        { icon: PackageCheck, color: "#10B981", label: "Ready for handover", value: hasOrders ? t.done : null,
          sub: hasOrders ? `${pct(t.done, t.orders)} · ready to load or complete` : "Needs Outstanding Orders", kind: "orders", preset: { only: "done" } },
        { icon: ClipboardCheck, color: "#8B5CF6", label: "With Mainfreight", value: hasPrealert || hasMfExport ? data.mainfreight.shipments : null,
          sub: hasPrealert || hasMfExport
              ? [hasMfExport && `${num(data.mfexport.total)} pre-alerted`, hasPrealert && `${data.prebooked.bookingRequested} booking requested · ${data.prebooked.booked} booked`].filter(Boolean).join(" · ")
              : "Needs a Mainfreight Prealert or export", kind: hasMfExport ? "mfexport" : "prealert" },
        { icon: Truck, color: "#06B6D4", label: "Shipped", value: hasShipped ? data.shipped.total : null,
          sub: hasShipped ? `${data.shipped.issues.length} need action · ${data.shipped.watch.length} to watch` : "Needs TNT Daily or the carrier report", kind: "shipped",
          alert: hasShipped && data.shipped.issues.length > 0 },
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            {cards.map(k => {
                const Icon = k.icon;
                const ready = k.value != null;
                const clickable = ready && !!k.kind;
                const Comp = clickable ? "button" : "div";
                return (
                    <Comp key={k.label} onClick={clickable ? () => open(k.kind, k.preset) : undefined}
                        className={`${card} p-4 text-left flex flex-col gap-2 ${clickable ? "hover:border-slate-500 transition-colors group" : ""}`}>
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${k.color}22` }}><Icon size={15} style={{ color: k.color }} /></span>
                            <span className="text-[11px] text-slate-400 leading-tight flex-1">{k.label}</span>
                            {clickable && <Maximize2 size={12} className="text-slate-600 group-hover:text-blue-400" />}
                        </div>
                        <p className={`text-2xl font-bold tabular-nums ${ready ? (k.alert ? "text-red-400" : "text-white") : "text-slate-600"}`}>{ready ? num(k.value) : "—"}</p>
                        <p className="text-[11px] text-slate-500 leading-snug">{k.sub}</p>
                    </Comp>
                );
            })}
        </div>
    );
}

function MissingReport({ title, text, onUpload }) {
    return (
        <div className={`${card} px-5 py-4 flex items-center justify-between gap-3 flex-wrap`}>
            <div>
                <p className="text-sm font-bold text-white">{title}</p>
                <p className="text-[12px] text-slate-500 mt-0.5">{text}</p>
            </div>
            {onUpload && (
                <button onClick={onUpload} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-500/40 text-sm text-green-400 hover:bg-green-500/10">
                    <FileSpreadsheet size={14} /> Upload report
                </button>
            )}
        </div>
    );
}

function TimelineCard({ m, onSelect, selected, contacts }) {
    const WHERE = { warehouse: "Dispatch · in the warehouse", shipped: "Shipped · with transport", prebooked: "Pre-booked", unknown: "Not found" };
    const firstShipped = m.steps.findIndex(s => s.phase === "shipped");
    return (
        <div className="bg-[#0D1520] border border-[#1F2937] rounded-xl p-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-bold text-white font-mono">{m.orderReference} <span className="text-slate-500 font-sans font-normal">· {m.consignment || "—"}</span></p>
                    <p className="text-[12px] text-slate-400 mt-0.5">{m.customer || "—"}{m.po && <> · PO <span className="font-mono">{m.po}</span></>}{m.carrier && <> · {m.carrier}</>}</p>
                    {m.status && <p className="text-[11px] text-sky-300 mt-0.5">{m.status}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-md border text-[11px] font-semibold ${m.where === "shipped" ? SEVERITY.ok : "bg-blue-500/15 text-blue-300 border-blue-500/30"}`}>
                        {WHERE[m.where]}{m.warehouseStage && m.where === "warehouse" ? ` · ${m.warehouseStage}` : ""}
                    </span>
                    {onSelect && (
                        <button onClick={onSelect} className={`flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${selected ? "border-sky-400/60 bg-sky-500/15 text-sky-300" : "border-[#334155] text-slate-400 hover:text-white"}`}>
                            <MapPin size={11} /> {selected ? "On the map" : "Show on map"}
                        </button>
                    )}
                </div>
            </div>
            {m.conflict && (
                <p className="text-[12px] text-amber-300 flex items-center gap-1.5"><AlertTriangle size={13} /> Shows as both still in dispatch and shipped — check which report is out of date.</p>
            )}

            {/* Stepper, grouped by phase */}
            <div className="overflow-x-auto pb-1">
                <div className="flex text-[10px] font-bold tracking-wider text-slate-500 mb-2">
                    <span style={{ flex: firstShipped }} className="text-center border-b border-blue-500/30 pb-1 mx-1">DISPATCH · WAREHOUSE</span>
                    <span style={{ flex: m.steps.length - firstShipped }} className="text-center border-b border-emerald-500/30 pb-1 mx-1">SHIPPED · WITH TRANSPORT</span>
                </div>
                <ol className="flex items-start">
                    {m.steps.map((s, i) => {
                        const dot = { done: "bg-emerald-500 border-emerald-500", current: "bg-blue-500 border-blue-400 ring-4 ring-blue-500/20", issue: "bg-red-500 border-red-400 ring-4 ring-red-500/20", todo: "bg-[#111827] border-[#334155]" }[s.state];
                        return (
                            <li key={s.key} className="flex-1 min-w-[62px] flex flex-col items-center text-center relative px-0.5">
                                {i > 0 && <span className={`absolute top-[7px] right-1/2 w-full h-0.5 ${m.steps[i - 1].state === "done" ? "bg-emerald-500/60" : "bg-[#1F2937]"}`} />}
                                <span className={`relative w-4 h-4 rounded-full border-2 ${dot}`} />
                                <span className={`mt-2 text-[10px] leading-tight ${s.state === "todo" ? "text-slate-600" : s.state === "issue" ? "text-red-300 font-semibold" : s.state === "current" ? "text-blue-300 font-semibold" : "text-slate-300"}`}>{s.label}</span>
                                {s.date && <span className="text-[10px] text-slate-500">{fmtDate(s.date)}</span>}
                            </li>
                        );
                    })}
                </ol>
            </div>

            <div className="grid grid-cols-1 gap-3">
                {m.orders.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Warehouse orders ({m.orders.length})</p>
                        <div className="max-h-48 overflow-auto rounded-lg border border-[#1F2937]">
                            <table className="w-full text-xs">
                                <tbody className="divide-y divide-[#1F2937]">
                                    {m.orders.map(o => (
                                        <tr key={o.id}>
                                            <td className="px-3 py-1.5 font-mono text-slate-300">{o.id}</td>
                                            <td className="px-3 py-1.5"><StagePill code={o.status} label={o.statusLabel} /></td>
                                            <td className="px-3 py-1.5 text-slate-400 text-right tabular-nums">{num(o.lines)} lines</td>
                                            <td className="px-3 py-1.5 text-slate-400 text-right tabular-nums">{num(o.quantity)} units</td>
                                            <td className={`px-3 py-1.5 text-right whitespace-nowrap ${o.overdueDays ? "text-red-400" : "text-slate-500"}`}>{o.shipBy ? `ship by ${fmtDate(o.shipBy)}` : "no ship-by"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {m.departed && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">With transport</p>
                        <div className="rounded-lg border border-[#1F2937] p-3 text-xs space-y-1.5">
                            <p className="flex items-center gap-2"><span className={`px-2 py-0.5 rounded-md border text-[11px] font-semibold ${SEVERITY[m.departed.severity]}`}>{m.departed.statusLabel}</span>{m.departed.reason && <span className="text-red-300">{m.departed.reason}</span>}</p>
                            <p className="text-slate-400">{m.departed.receiver} · {m.departed.suburb}{m.departed.site ? ` · ${m.departed.site}` : ""}{m.departed.siteOdd ? " (odd site code — check with the carrier)" : ""}</p>
                            <p className="text-slate-400">Required delivery <span className="text-slate-200">{m.departed.requiredDelivery ? fmtDate(m.departed.requiredDelivery) : m.departed.requiredDeliveryText || "—"}</span> · Booking <span className="text-slate-200">{fmtDate(m.departed.booking)}</span></p>
                            {m.departed.comments && <p className="text-slate-500 italic">“{m.departed.comments}”</p>}
                        </div>
                    </div>
                )}
                {m.tnt && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">With TNT</p>
                        <div className="rounded-lg border border-[#1F2937] p-3 text-xs space-y-1.5">
                            <p className="flex items-center gap-2"><span className={`px-2 py-0.5 rounded-md border text-[11px] font-semibold ${SEVERITY[m.tnt.severity]}`}>{m.tnt.statusLabel}</span>{m.tnt.incident && <span className="text-amber-300">{m.tnt.incident}</span>}</p>
                            <p className="text-slate-400">{m.tnt.receiver} · {m.tnt.suburb}{m.tnt.service ? ` · ${m.tnt.service}` : ""} · dispatched {fmtDate(m.tnt.dispatched)}{m.tnt.deliveryId ? ` · delivery ${m.tnt.deliveryId}` : ""}</p>
                            {m.tnt.comments && <p className="text-slate-500 italic">“{m.tnt.comments}”</p>}
                        </div>
                    </div>
                )}
                {m.prealert && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Pre-booked with Mainfreight</p>
                        <div className="rounded-lg border border-[#1F2937] p-3 text-xs space-y-1">
                            <p className="text-slate-300">Shipment {m.prealert.shipment} · {m.prealert.receiver} · {m.prealert.city} {m.prealert.state}</p>
                            <p className="text-slate-400">{num(m.prealert.pallets)} pallet(s) · {num(m.prealert.cartons)} cartons · pick-up {fmtDate(m.prealert.pickup)} · delivery {fmtDate(m.prealert.delivery)} · booking {m.prealert.booking ? fmtDate(m.prealert.booking) : m.prealert.bookingRequested ? <span className="text-amber-300">requested</span> : "—"}</p>
                            {m.prealert.po && <p className="text-slate-500">Customer PO <span className="font-mono">{m.prealert.po}</span></p>}
                        </div>
                    </div>
                )}
                {m.mfexport && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Pre-alerted to Mainfreight</p>
                        <div className="rounded-lg border border-[#1F2937] p-3 text-xs space-y-1">
                            <p className="text-slate-300">Shipment {m.mfexport.shipment} · {m.mfexport.receiver} · {m.mfexport.city} {m.mfexport.state} {m.mfexport.postcode}</p>
                            <p className="text-slate-400">{num(m.mfexport.pallets)} pallet(s) · {num(m.mfexport.cartons)} cartons · {num(m.mfexport.weightKg)} kg · {m.mfexport.cubeCbm} m³</p>
                            <p className="text-slate-400">Pick-up {fmtDate(m.mfexport.pickupFrom)}{m.mfexport.pickupTo && m.mfexport.pickupTo !== m.mfexport.pickupFrom ? ` – ${fmtDate(m.mfexport.pickupTo)}` : ""} · delivery {fmtDate(m.mfexport.delivery)}
                                {m.mfexport.cancel && <> · <span className={m.mfexport.cancelPassed ? "text-red-400" : m.mfexport.cancelSoon ? "text-amber-300" : ""}>cancel {fmtDate(m.mfexport.cancel)}</span></>}</p>
                            <p className="text-slate-500">{m.mfexport.po && <>Customer PO <span className="font-mono">{m.mfexport.po}</span> · </>}SAP delivery <span className="font-mono">{m.mfexport.deliveryId || "—"}</span>{m.mfexport.shipTo && <> · ship-to {m.mfexport.shipTo}</>}</p>
                        </div>
                    </div>
                )}
                {m.prebooking && (
                    <div className="text-xs text-slate-400 rounded-lg border border-[#1F2937] p-3">
                        Pre-booking {m.prebooking.shipment} · {m.prebooking.state || "—"} · dispatch {fmtDate(m.prebooking.dispatch)} · booking {fmtDate(m.prebooking.booking)}
                    </div>
                )}
                {m.returns.length > 0 && (
                    <div className="text-xs text-slate-400 rounded-lg border border-[#1F2937] p-3">
                        Returned: {m.returns.map(r => `${r.connote}${r.comments ? ` (${r.comments})` : ""}`).join(", ")}
                    </div>
                )}
                {contacts && <ShipmentContacts m={m} />}
            </div>
        </div>
    );
}

// ── Dispatch (warehouse → handover) ──────────────────────────────────────────
function DispatchPanel({ data, open }) {
    const { pipeline } = data;
    const t = pipeline.totals;
    const ordersOverlay = preset => open({ kind: "orders", preset });
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {data.reports.prealert ? (
                    <div className={card}>
                        <SectionTitle icon={Truck} title="Pre-booked with Mainfreight" sub={`Prealert report of ${fmtDate(data.reports.prealert.reportDate)} · Metro + Interstate`}
                            right={<OpenButton onClick={() => open({ kind: "prealert" })}>All ({data.prebooked.total})</OpenButton>} />
                        <div className="grid grid-cols-3 gap-2 px-5 pb-4">
                            {[["Shipments", data.prebooked.total, "text-white", {}], ["Also at the 3PL", data.prebooked.inOutstanding, "text-sky-300", { only: "inOutstanding" }],
                              ["Not in Outstanding", data.prebooked.notInOutstanding.length, data.prebooked.notInOutstanding.length ? "text-amber-300" : "text-white", { only: "notInOutstanding" }]].map(([label, n, tone, preset]) => (
                                <button key={label} onClick={() => open({ kind: "prealert", preset })} className="text-left rounded-lg border border-[#1F2937] hover:border-blue-500/50 px-3 py-2.5">
                                    <span className="block text-[11px] text-slate-500">{label}</span>
                                    <span className={`block text-lg font-bold tabular-nums ${tone}`}>{num(n)}</span>
                                </button>
                            ))}
                        </div>
                        <p className="px-5 pb-4 -mt-2 text-[11px] text-slate-500">{data.prebooked.bookingRequested} with booking REQUESTED · {data.prebooked.booked} with a booking date
                            {data.prebooked.metro + data.prebooked.interstate > 0 && <> · {data.prebooked.metro} Metro · {data.prebooked.interstate} Interstate · {num(data.prebooked.pallets)} pallets · {num(data.prebooked.cartons)} cartons</>}</p>
                    </div>
                ) : (
                    <div className={`${card} px-5 py-4 text-[12px] text-slate-500`}>Upload the Mainfreight Prealert report to see which shipments are pre-booked.</div>
                )}
                {data.reports.mfexport ? (
                    <div className={card}>
                        <SectionTitle icon={Truck} title="Pre-alerted to Mainfreight" sub={`Mainfreight export of ${fmtDate(data.reports.mfexport.reportDate)} · open shipments`}
                            right={<OpenButton onClick={() => open({ kind: "mfexport" })}>All ({data.mfexport.total})</OpenButton>} />
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-5 pb-4">
                            {[["Shipments", data.mfexport.total, "text-white", {}],
                              ["Not in Outstanding", data.mfexport.notInOutstanding.length, data.mfexport.notInOutstanding.length ? "text-amber-300" : "text-white", { only: "notInOutstanding" }],
                              ["Not pre-booked yet", data.mfexport.total - data.mfexport.alsoPrebooked, "text-sky-300", { only: "notPrebooked" }],
                              ["Cancel date ≤ 3 days", data.mfexport.cancelSoon.length, data.mfexport.cancelSoon.length ? "text-red-400" : "text-white", { only: "cancelSoon" }]].map(([label, n, tone, preset]) => (
                                <button key={label} onClick={() => open({ kind: "mfexport", preset })} className="text-left rounded-lg border border-[#1F2937] hover:border-blue-500/50 px-3 py-2.5">
                                    <span className="block text-[11px] text-slate-500">{label}</span>
                                    <span className={`block text-lg font-bold tabular-nums ${tone}`}>{num(n)}</span>
                                </button>
                            ))}
                        </div>
                        <p className="px-5 pb-4 -mt-2 text-[11px] text-slate-500">
                            {num(data.mfexport.pallets)} pallets · {num(data.mfexport.cartons)} cartons · {num(Math.round(data.mfexport.weightKg))} kg · {num(Math.round(data.mfexport.cubeCbm * 10) / 10)} m³ · {data.mfexport.byState.map(([st, n]) => `${st} ${n}`).join(" · ")}
                        </p>
                    </div>
                ) : (
                    <div className={`${card} px-5 py-4 text-[12px] text-slate-500`}>Upload the Mainfreight pre-alert export (Power BI) to see every open Mainfreight shipment with weight, cube and cancel dates.</div>
                )}
                <div className={card}>
                    <SectionTitle icon={Truck} title="By carrier" sub="From the Outstanding Orders manifest code (MANFRE, TNT, DTS) or the consignment (PRIM)" />
                    <div className="flex flex-wrap gap-2 px-5 pb-4">
                        {pipeline.carriers.map(c => (
                            <button key={c.name} onClick={() => ordersOverlay({ carrier: c.name })}
                                className={`text-left rounded-lg border px-3 py-2 hover:border-blue-500/50 ${/confirm/i.test(c.name) ? "border-amber-500/30" : "border-[#1F2937]"}`}>
                                <span className={`block text-[11px] ${/confirm/i.test(c.name) ? "text-amber-300" : "text-slate-400"}`}>{c.name}</span>
                                <span className="block text-base font-bold tabular-nums text-white">{num(c.orders)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className={card}>
                <SectionTitle icon={Warehouse} title="Dispatch pipeline"
                    sub={`Outstanding Orders report of ${fmtDate(data.reports.outstanding.reportDate)} · click a stage to open its orders`}
                    right={<div className="flex gap-2">
                        <OpenButton onClick={() => ordersOverlay({})}>All orders ({num(t.orders)})</OpenButton>
                        <OpenButton icon={Users} onClick={() => open({ kind: "customers" })}>By customer</OpenButton>
                    </div>} />
                <div className="px-5">
                    <div className="flex h-3 rounded-full overflow-hidden bg-[#0D1520]">
                        {pipeline.stages.filter(s => s.orders).map(s => (
                            <button key={s.code} title={`${s.label}: ${s.orders}`} onClick={() => ordersOverlay({ stage: s.code })}
                                className="h-full hover:opacity-80" style={{ width: `${(s.orders / t.orders) * 100}%`, background: STAGE_COLOR[s.code] }} />
                        ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-600 mt-1"><span>Allocated</span><span>Handover to transport →</span></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 px-5 py-4">
                    {pipeline.stages.map((s, i) => (
                        <button key={s.code} onClick={() => ordersOverlay({ stage: s.code })}
                            className="text-left rounded-lg border border-[#1F2937] hover:border-blue-500/50 hover:bg-blue-500/[0.06] px-3 py-2.5 transition-colors group">
                            <span className="flex items-center justify-between gap-1.5 text-[11px] text-slate-400">
                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLOR[s.code] }} />{i + 1}. {s.label}</span>
                                <Maximize2 size={11} className="text-slate-700 group-hover:text-blue-400" />
                            </span>
                            <span className="block text-lg font-bold text-white tabular-nums mt-0.5">{num(s.orders)}</span>
                            <span className="block text-[10px] text-slate-500">{num(s.lines)} lines · {num(s.units)} units{s.overdue ? <span className="text-red-400"> · {s.overdue} late</span> : null}</span>
                        </button>
                    ))}
                </div>
                {pipeline.unknown > 0 && <p className="px-5 pb-3 text-[11px] text-amber-300">{pipeline.unknown} orders have a status that isn't a known warehouse stage.</p>}
            </div>
        </div>
    );
}

function OrdersOverlay({ data, preset = {}, onClose, onPick }) {
    const { orders, pipeline } = data;
    const [stage, setStage] = useState(preset.stage || "");
    const [only, setOnly] = useState(preset.only || "");
    const [carrier, setCarrier] = useState(preset.carrier || "");
    const [q, setQ] = useState("");
    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return orders.filter(o =>
            (!stage || o.status === stage) && (!carrier || o.carrier === carrier)
            && (!only || (only === "notPicked" && o.notPicked) || (only === "overdue" && o.overdueDays > 0) || (only === "noShipBy" && !o.shipBy) || (only === "done" && o.done))
            && (!s || [o.id, o.orderReference, o.consignment, o.customer].some(v => (v || "").toLowerCase().includes(s))));
    }, [orders, stage, q, only, carrier]);

    return (
        <Overlay onClose={onClose} title="Dispatch — orders in the warehouse"
            sub={`Outstanding Orders report of ${fmtDate(data.reports.outstanding.reportDate)} · click a row to see it on the timeline and map`}
            toolbar={<>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Order, reference, consignment, customer" className={`w-72 max-w-full ${inputDark} pl-8 pr-3 py-1.5`} />
                </div>
                <select value={stage} onChange={e => setStage(e.target.value)} className={`${inputDark} px-2.5 py-1.5`}>
                    <option value="">All stages</option>
                    {pipeline.stages.map(s => <option key={s.code} value={s.code}>{s.label} ({s.orders})</option>)}
                </select>
                <select value={carrier} onChange={e => setCarrier(e.target.value)} className={`${inputDark} px-2.5 py-1.5`}>
                    <option value="">All carriers</option>
                    {pipeline.carriers.map(c => <option key={c.name} value={c.name}>{c.name} ({c.orders})</option>)}
                </select>
                {[["", "All"], ["notPicked", "Not picked"], ["overdue", "Past ship-by"], ["noShipBy", "No ship-by"], ["done", "Ready for handover"]].map(([id, label]) => (
                    <button key={id} onClick={() => setOnly(id)} className={`px-2.5 py-1.5 rounded-lg text-xs ${only === id ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>{label}</button>
                ))}
                <span className="ml-auto text-[11px] text-slate-500">{num(shown.length)} of {num(orders.length)} orders</span>
            </>}>
            <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-[#0D1520] shadow-[0_1px_0_#1F2937]">
                    <tr>{["Order", "Order ref / consignment", "Customer", "Stage", "Lines", "Units", "Created", "Ship by", "Pick lists", ""].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1F2937]">
                    {shown.map(o => (
                        <tr key={o.id} onClick={() => onPick(o.orderReference)} className="hover:bg-[#1F2937]/40 cursor-pointer group">
                            <td className="px-4 py-2 font-mono text-slate-200">{o.id}</td>
                            <td className="px-4 py-2 font-mono text-slate-400">{o.orderReference}<span className="block text-[10px] text-slate-600">{o.consignment}</span></td>
                            <td className="px-4 py-2 text-slate-300 max-w-[280px] truncate" title={o.customer}>{o.customer || "—"}</td>
                            <td className="px-4 py-2"><StagePill code={o.status} label={o.statusLabel} /></td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(o.lines)}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(o.quantity)}</td>
                            <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtDate(o.created)}</td>
                            <td className={`px-4 py-2 whitespace-nowrap ${o.overdueDays ? "text-red-400 font-semibold" : "text-slate-400"}`}>
                                {o.shipBy ? fmtDate(o.shipBy) : <span className="text-slate-600">—</span>}{o.overdueDays ? ` · ${o.overdueDays}d late` : ""}
                            </td>
                            <td className="px-4 py-2 text-slate-500">{o.pickLists.length || "—"}</td>
                            <td className="px-2 py-2 text-slate-600 group-hover:text-blue-400"><ChevronRight size={14} /></td>
                        </tr>
                    ))}
                    {!shown.length && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">No orders match.</td></tr>}
                </tbody>
            </table>
        </Overlay>
    );
}

// Inbound Pilot lines for a searched PO (rules: PO_CLASSES / INBOUND_STATUS)
function InboundMatches({ rows }) {
    const CLASS_TONE = { FOB: "text-sky-300", STO: "text-violet-300", "OTHER-MDPO": "text-amber-300", UNCLASSIFIED: "text-slate-400" };
    return (
        <div className="bg-[#0D1520] border border-[#1F2937] rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Inbound — Pilot Report ({rows.length} PO line{rows.length !== 1 ? "s" : ""})</p>
            <div className="max-h-56 overflow-auto">
                <table className="w-full text-xs">
                    <thead><tr>{["PO", "Class", "Line", "SKU", "Status", "ETA", "Container", "Carrier"].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-[#1F2937]">
                        {rows.map((r, i) => (
                            <tr key={`${r.po}-${r.line}-${r.sku}-${i}`}>
                                <td className="px-4 py-1.5 font-mono text-slate-200">{r.po}</td>
                                <td className={`px-4 py-1.5 font-semibold ${CLASS_TONE[r.poClass] || "text-slate-400"}`}>{r.poClass}</td>
                                <td className="px-4 py-1.5 text-slate-400">{r.line}</td>
                                <td className="px-4 py-1.5 font-mono text-slate-400">{r.sku}</td>
                                <td className="px-4 py-1.5"><span className={r.incoming ? "text-sky-300" : "text-emerald-300"}>{r.bucket}</span></td>
                                <td className="px-4 py-1.5 text-slate-400 whitespace-nowrap">{fmtDate(r.eta)}</td>
                                <td className="px-4 py-1.5 font-mono text-slate-500">{r.container || "—"}</td>
                                <td className="px-4 py-1.5 text-slate-500">{r.carrier || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function PrealertOverlay({ prebooked, report, preset = {}, onClose, onPick }) {
    const [only, setOnly] = useState(preset.only || "");
    const [q, setQ] = useState("");
    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return prebooked.rows.filter(p =>
            (!only || (only === "inOutstanding" && p.inOutstanding) || (only === "notInOutstanding" && !p.inOutstanding) || (only === "requested" && p.bookingRequested))
            && (!s || [p.shipment, p.receiver, p.city, p.po].some(v => String(v || "").toLowerCase().includes(s)) || poMatch(p.po, s)));
    }, [prebooked.rows, only, q]);
    return (
        <Overlay onClose={onClose} title="Dispatch — pre-booked with Mainfreight"
            sub={`Prealert report of ${fmtDate(report?.reportDate)} · Metro + Interstate · click a row to see it on the timeline and map`}
            toolbar={<>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Shipment, receiver, city, customer PO" className={`w-72 max-w-full ${inputDark} pl-8 pr-3 py-1.5`} />
                </div>
                {[["", "All"], ["inOutstanding", "Also at the 3PL"], ["notInOutstanding", "Not in Outstanding"], ["requested", "Booking requested"]].map(([id, label]) => (
                    <button key={id} onClick={() => setOnly(id)} className={`px-2.5 py-1.5 rounded-lg text-xs ${only === id ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>{label}</button>
                ))}
                <span className="ml-auto text-[11px] text-slate-500">{num(shown.length)} of {num(prebooked.total)} shipments</span>
            </>}>
            <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-[#0D1520] shadow-[0_1px_0_#1F2937]">
                    <tr>{["Shipment", "Sheet", "Receiver", "City", "Customer PO", "Pallets", "Cartons", "Pick-up", "Delivery", "Booking", "At 3PL", ""].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1F2937]">
                    {shown.map(p => (
                        <tr key={p.key} onClick={() => onPick(p.shipment)} className="hover:bg-[#1F2937]/40 cursor-pointer group">
                            <td className="px-4 py-2 font-mono text-slate-200">{p.shipment}</td>
                            <td className="px-4 py-2 text-slate-500">{p.region || (/interstate/i.test(p.sheet) ? "Interstate" : "Metro")}</td>
                            <td className="px-4 py-2 text-slate-300 max-w-[240px] truncate" title={p.receiver}>{p.receiver}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{p.city} {p.state}</td>
                            <td className="px-4 py-2 font-mono text-slate-400 max-w-[160px] truncate" title={p.po}>{p.po || "—"}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(p.pallets)}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(p.cartons)}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{fmtDate(p.pickup)}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{fmtDate(p.delivery)}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{p.booking ? <span className="text-slate-300">{fmtDate(p.booking)}</span> : p.bookingRequested ? <span className="text-amber-300">Requested</span> : "—"}</td>
                            <td className="px-4 py-2">{p.inOutstanding ? <span className="text-sky-300">Yes</span> : <span className="text-amber-300">No</span>}</td>
                            <td className="px-2 py-2 text-slate-600 group-hover:text-blue-400"><ChevronRight size={14} /></td>
                        </tr>
                    ))}
                    {!shown.length && <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500">No shipments match.</td></tr>}
                </tbody>
            </table>
        </Overlay>
    );
}

function MfExportOverlay({ mf, report, preset = {}, onClose, onPick }) {
    const [only, setOnly] = useState(preset.only || "");
    const [q, setQ] = useState("");
    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return mf.rows.filter(p =>
            (!only || (only === "notInOutstanding" && !p.inOutstanding) || (only === "notPrebooked" && !p.prebooked) || (only === "cancelSoon" && p.cancelSoon))
            && (!s || [p.shipment, p.receiver, p.city, p.po, p.deliveryId, p.postcode].some(v => String(v || "").toLowerCase().includes(s)) || poMatch(p.po, s)));
    }, [mf.rows, only, q]);
    return (
        <Overlay onClose={onClose} title="Dispatch — pre-alerted to Mainfreight"
            sub={`Mainfreight export of ${fmtDate(report?.reportDate)} · click a row to see it on the timeline and map`}
            toolbar={<>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Shipment, receiver, city, customer PO, SAP delivery" className={`w-80 max-w-full ${inputDark} pl-8 pr-3 py-1.5`} />
                </div>
                {[["", "All"], ["notInOutstanding", "Not in Outstanding"], ["notPrebooked", "Not pre-booked yet"], ["cancelSoon", "Cancel date ≤ 3 days"]].map(([id, label]) => (
                    <button key={id} onClick={() => setOnly(id)} className={`px-2.5 py-1.5 rounded-lg text-xs ${only === id ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>{label}</button>
                ))}
                <span className="ml-auto text-[11px] text-slate-500">{num(shown.length)} of {num(mf.total)} shipments</span>
            </>}>
            <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-[#0D1520] shadow-[0_1px_0_#1F2937]">
                    <tr>{["Shipment", "Receiver", "City", "Customer PO", "Pallets", "Cartons", "Kg", "m³", "Pick-up", "Delivery", "Cancel", "SAP delivery", "At 3PL", ""].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1F2937]">
                    {shown.map(p => (
                        <tr key={p.key} onClick={() => onPick(p.shipment)} className="hover:bg-[#1F2937]/40 cursor-pointer group">
                            <td className="px-4 py-2 font-mono text-slate-200">{p.shipment}{p.prebooked && <span className="ml-1.5 px-1 py-px rounded bg-violet-500/15 text-violet-300 text-[9px] font-bold font-sans" title="Also in the Prealert prebookings">PRE</span>}</td>
                            <td className="px-4 py-2 text-slate-300 max-w-[220px] truncate" title={p.receiver}>{p.receiver}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{p.city} {p.state} {p.postcode}</td>
                            <td className="px-4 py-2 font-mono text-slate-400 max-w-[150px] truncate" title={p.po}>{p.po || "—"}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(p.pallets)}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(p.cartons)}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{num(p.weightKg)}</td>
                            <td className="px-4 py-2 text-slate-400 tabular-nums">{p.cubeCbm ?? "—"}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{fmtDate(p.pickupFrom)}{p.pickupTo && p.pickupTo !== p.pickupFrom ? ` – ${fmtDate(p.pickupTo)}` : ""}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{fmtDate(p.delivery)}</td>
                            <td className={`px-4 py-2 whitespace-nowrap ${p.cancelPassed ? "text-red-400" : p.cancelSoon ? "text-amber-300" : "text-slate-400"}`}>{p.cancel ? fmtDate(p.cancel) : "—"}</td>
                            <td className="px-4 py-2 font-mono text-slate-400">{p.deliveryId || "—"}</td>
                            <td className="px-4 py-2">{p.inOutstanding ? <span className="text-sky-300">Yes</span> : <span className="text-amber-300">No</span>}</td>
                            <td className="px-2 py-2 text-slate-600 group-hover:text-blue-400"><ChevronRight size={14} /></td>
                        </tr>
                    ))}
                    {!shown.length && <tr><td colSpan={14} className="px-4 py-10 text-center text-slate-500">No shipments match.</td></tr>}
                </tbody>
            </table>
        </Overlay>
    );
}

function CustomersOverlay({ customers, onClose }) {
    return (
        <Overlay onClose={onClose} title="Dispatch — busiest ship-to customers" sub="Orders still in the warehouse, by ship-to name">
            <div className="p-5 space-y-2">
                {customers.map(c => (
                    <div key={c.name} className="flex items-center gap-4 text-sm">
                        <span className="flex-1 truncate text-slate-200" title={c.name}>{c.name}</span>
                        <span className="w-64 h-2 rounded-full bg-[#111827] overflow-hidden">
                            <span className="block h-full bg-blue-500" style={{ width: `${(c.orders / customers[0].orders) * 100}%` }} />
                        </span>
                        <span className="w-24 text-right text-slate-300 tabular-nums">{c.orders} orders</span>
                        <span className={`w-28 text-right tabular-nums ${c.notPicked ? "text-amber-300" : "text-slate-600"}`}>{c.notPicked} not picked</span>
                        <span className="w-28 text-right text-slate-500 tabular-nums">{num(c.units)} units</span>
                    </div>
                ))}
            </div>
        </Overlay>
    );
}

// ── Shipped (with transport → customer) ──────────────────────────────────────
function ShippedPanel({ shipped, open }) {
    const count = code => shipped.byStatus.find(b => b.code === code)?.count || 0;
    const shippedOverlay = preset => open({ kind: "shipped", preset });
    const onTheWay = count("IN_TRANSIT") + count("ARRIVED_BOOKED") + count("OUT_FOR_DELIVERY") + count("ALLOCATED_LINEHAUL") + count("ALLOCATED_FOR_COLLECTION") + count("AWAITING_COLLECTION");
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="With transport" value={num(shipped.total)} sub="HSB consignments handed to the carrier" onClick={() => shippedOverlay({})} />
                <Kpi label="Need action" value={num(shipped.issues.length)} tone={shipped.issues.length ? "text-red-400" : "text-white"} sub="awaiting booking, ASN issues, confirmations" onClick={() => shippedOverlay({ severity: "issue" })} />
                <Kpi label="On the way" value={num(onTheWay)} tone="text-sky-300" sub={`in transit, booked or out for delivery${shipped.late ? ` · ${shipped.late} past required date` : ""}`} onClick={() => shippedOverlay({ severity: "ok" })} />
                <Kpi label="Awaiting POD / delivered" value={`${num(count("AWAITING_POD"))} / ${num(count("DELIVERED"))}`} tone="text-emerald-300" sub="proof of delivery outstanding / delivered" onClick={() => shippedOverlay({ severity: "watch" })} />
            </div>
            <div className={card}>
                <SectionTitle icon={Truck} title="Shipped — by carrier status" sub="Click a status to open its consignments"
                    right={<div className="flex gap-2">
                        <OpenButton onClick={() => shippedOverlay({})}>All consignments ({num(shipped.total)})</OpenButton>
                        <OpenButton icon={AlertTriangle} onClick={() => shippedOverlay({ severity: "issue" })}>Issues ({shipped.issues.length})</OpenButton>
                    </div>} />
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 px-5 pb-4">
                    {shipped.byStatus.map(b => {
                        const Icon = b.severity === "issue" ? AlertTriangle : b.code === "AWAITING_POD" ? Hourglass : b.code === "DELIVERED" ? ClipboardCheck : b.code === "OUT_FOR_DELIVERY" ? PackageCheck : Truck;
                        return (
                            <button key={b.code} onClick={() => shippedOverlay({ status: b.code })}
                                className={`text-left rounded-lg border px-3 py-2.5 transition-colors hover:brightness-125 ${SEVERITY[b.severity] || SEVERITY.ok}`}>
                                <span className="flex items-center gap-1.5 text-[11px]"><Icon size={12} /> {b.label}</span>
                                <span className="block text-lg font-bold tabular-nums mt-0.5 text-white">{b.count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function ShippedOverlay({ shipped, preset = {}, onClose, onPick }) {
    const [severity, setSeverity] = useState(preset.severity || "");
    const [status, setStatus] = useState(preset.status || "");
    const [q, setQ] = useState("");
    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return shipped.rows.filter(d =>
            (!severity || d.severity === severity) && (!status || d.status === status)
            && (!s || [d.connote, d.receiver, d.suburb, d.po, d.comments, d.site].some(v => (v || "").toLowerCase().includes(s)) || poMatch(d.po, s)));
    }, [shipped.rows, severity, status, q]);

    return (
        <Overlay onClose={onClose} title="Shipped — consignments with transport"
            sub="Carrier report · click a row to see it on the timeline and map"
            toolbar={<>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Connote, receiver, suburb, customer PO, comment" className={`w-72 max-w-full ${inputDark} pl-8 pr-3 py-1.5`} />
                </div>
                <select value={status} onChange={e => setStatus(e.target.value)} className={`${inputDark} px-2.5 py-1.5`}>
                    <option value="">All statuses</option>
                    {shipped.byStatus.map(b => <option key={b.code} value={b.code}>{b.label} ({b.count})</option>)}
                </select>
                {[["", "All"], ["issue", "Need action"], ["watch", "Awaiting POD"], ["ok", "On track"]].map(([id, label]) => (
                    <button key={id} onClick={() => setSeverity(id)} className={`px-2.5 py-1.5 rounded-lg text-xs ${severity === id ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>{label}</button>
                ))}
                <span className="ml-auto text-[11px] text-slate-500">{num(shown.length)} of {num(shipped.total)} consignments</span>
            </>}>
            <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-[#0D1520] shadow-[0_1px_0_#1F2937]">
                    <tr>{["Connote", "Source", "Receiver", "Site", "PO", "Status", "Issue", "Required delivery", "Booking", "Comments", ""].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1F2937]">
                    {shown.map(d => (
                        <tr key={d.key} onClick={() => onPick(d.connote)} className="hover:bg-[#1F2937]/40 cursor-pointer group">
                            <td className="px-4 py-2 font-mono text-slate-200 whitespace-nowrap">{d.connote}{d.orderReference && <span className="block text-[10px] text-slate-600">ref {d.orderReference}</span>}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{d.source}</td>
                            <td className="px-4 py-2 text-slate-300 max-w-[220px] truncate" title={d.receiver}>{d.receiver}<span className="block text-[10px] text-slate-500">{d.suburb}</span></td>
                            <td className="px-4 py-2 font-mono text-slate-400">{d.site || "—"}{d.siteOdd && <span className="ml-1 px-1 py-px rounded bg-amber-500/15 text-amber-300 text-[9px] font-bold font-sans" title="Excel stored this site code as a date — check it with the carrier">ODD</span>}</td>
                            <td className="px-4 py-2 font-mono text-slate-400 max-w-[150px] truncate" title={d.po}>{d.po}</td>
                            <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-md border text-[11px] font-semibold whitespace-nowrap ${SEVERITY[d.severity]}`}>{d.statusLabel}</span></td>
                            <td className="px-4 py-2 text-red-300 whitespace-nowrap">{d.severity !== "ok" ? d.reason || "—" : d.late ? <span className="text-amber-300">Past required date</span> : "—"}</td>
                            <td className={`px-4 py-2 whitespace-nowrap ${d.late ? "text-red-400" : "text-slate-400"}`}>{d.requiredDelivery ? fmtDate(d.requiredDelivery) : d.requiredDeliveryText || "—"}</td>
                            <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{fmtDate(d.booking)}</td>
                            <td className="px-4 py-2 text-slate-500 max-w-[280px] truncate" title={d.comments}>{d.comments || "—"}</td>
                            <td className="px-2 py-2 text-slate-600 group-hover:text-blue-400"><ChevronRight size={14} /></td>
                        </tr>
                    ))}
                    {!shown.length && <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-500">No consignments match.</td></tr>}
                </tbody>
            </table>
        </Overlay>
    );
}

function LinkChecks({ checks, hasBoth }) {
    const items = [
        { ok: checks.alsoStillOutstanding.length === 0, label: "Both in dispatch and shipped", value: checks.alsoStillOutstanding.length,
          detail: checks.alsoStillOutstanding.slice(0, 5).map(x => `${x.orderReference} (${x.warehouseStage || "—"})`).join(", "), need: true },
        { ok: checks.prebookedStillOutstanding.length === 0, label: "Pre-booked but still in the warehouse", value: checks.prebookedStillOutstanding.length,
          detail: checks.prebookedStillOutstanding.slice(0, 5).map(x => x.orderReference).join(", "), need: true, soft: true },
        { ok: checks.returnsOfOutstanding.length === 0, label: "Returns of orders still in dispatch", value: checks.returnsOfOutstanding.length,
          detail: checks.returnsOfOutstanding.slice(0, 5).map(x => x.orderReference).join(", "), need: true },
        { ok: checks.carrierRefsWithoutJoinKey.length === 0, label: "Shipped rows without an order reference", value: checks.carrierRefsWithoutJoinKey.length,
          detail: checks.carrierRefsWithoutJoinKey.slice(0, 5).join(", "), soft: true },
        { ok: !(checks.oddDeliverySites || []).length, label: "Odd delivery site codes", value: (checks.oddDeliverySites || []).length,
          detail: "Excel stored these site codes as dates (e.g. Mar-40) — check them with the carrier", soft: true },
        { ok: !checks.prealertNotInOutstanding?.length, label: "Pre-booked but not in Outstanding Orders", value: checks.prealertNotInOutstanding?.length || 0,
          detail: (checks.prealertNotInOutstanding || []).slice(0, 6).join(", ") + " — probably already left the 3PL", soft: true },
        { ok: !checks.primToConfirm, label: "PRIM consignments — confirm carrier", value: checks.primToConfirm || 0,
          detail: "No carrier code on these Outstanding rows (rules: CARRIER_BY_CONSIGNMENT)", soft: true },
    ];
    return (
        <div className={card}>
            <SectionTitle icon={Link2} title="Link checks" sub="Dispatch (Outstanding Orders) ↔ Shipped (carrier report), joined on the order reference" />
            <ul className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                {items.map(it => {
                    const skipped = it.need && !hasBoth;
                    const Icon = skipped ? Link2 : it.ok ? ShieldCheck : AlertTriangle;
                    const tone = skipped ? "text-slate-600" : it.ok ? "text-emerald-400" : it.soft ? "text-amber-300" : "text-red-400";
                    return (
                        <li key={it.label} className="flex gap-2.5 rounded-lg border border-[#1F2937] px-3 py-2.5">
                            <Icon size={15} className={`${tone} shrink-0 mt-0.5`} />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-slate-300 flex justify-between gap-2">
                                    <span>{it.label}</span>
                                    <span className={`font-bold tabular-nums ${tone}`}>{skipped ? "—" : it.value}</span>
                                </p>
                                {skipped ? <p className="text-[11px] text-slate-600">Needs both reports</p>
                                    : !it.ok && it.detail && <p className="text-[11px] text-slate-500 truncate" title={it.detail}>{it.detail}</p>}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
